import { useEffect, useMemo, useRef, useState } from "react";
import api from "../lib/api";
import { PageHeader, Surface, Empty, StatCard } from "../components/Primitives";
import { Truck, UserPlus, PlayCircle, CheckCircle2, XCircle, Phone, MapPin, Trash2, Plus, RotateCcw, Camera, X, MessageCircle, Copy } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";

const fmt = (n) => `${(Math.round(n) || 0).toLocaleString("fr-FR")} F`;

const STATUS_LABELS = {
  en_attente: { label: "À assigner", cls: "bg-gray-100 text-gray-700" },
  assigne: { label: "Assigné", cls: "bg-blue-100 text-blue-700" },
  en_route: { label: "En route", cls: "bg-amber-100 text-amber-800" },
  livree: { label: "Livrée", cls: "bg-[#F8E8F1] text-[#8E3A6D]" },
  echec: { label: "Échec", cls: "bg-red-100 text-red-700" },
};

function getDeliveryState(o) {
  if (o.status === "livree") return "livree";
  if (o.delivery_status === "echec") return "echec";
  if (o.delivery_status === "en_route") return "en_route";
  if (o.assigned_driver_id) return "assigne";
  return "en_attente";
}

// ---- Signature pad ----
function SignaturePad({ onChange }) {
  const canvasRef = useRef(null);
  const drawing = useRef(false);
  const last = useRef({ x: 0, y: 0 });

  useEffect(() => {
    const c = canvasRef.current;
    const ctx = c.getContext("2d");
    ctx.lineWidth = 2.4;
    ctx.lineCap = "round";
    ctx.strokeStyle = "#020A18";
    ctx.fillStyle = "#fff";
    ctx.fillRect(0, 0, c.width, c.height);
  }, []);

  const pos = (e) => {
    const c = canvasRef.current;
    const r = c.getBoundingClientRect();
    const t = e.touches ? e.touches[0] : e;
    return { x: ((t.clientX - r.left) * c.width) / r.width, y: ((t.clientY - r.top) * c.height) / r.height };
  };
  const start = (e) => { e.preventDefault(); drawing.current = true; last.current = pos(e); };
  const move = (e) => {
    if (!drawing.current) return;
    e.preventDefault();
    const c = canvasRef.current;
    const ctx = c.getContext("2d");
    const p = pos(e);
    ctx.beginPath(); ctx.moveTo(last.current.x, last.current.y); ctx.lineTo(p.x, p.y); ctx.stroke();
    last.current = p;
  };
  const end = () => {
    if (!drawing.current) return;
    drawing.current = false;
    onChange(canvasRef.current.toDataURL("image/png"));
  };
  const clear = () => {
    const c = canvasRef.current;
    const ctx = c.getContext("2d");
    ctx.fillStyle = "#fff"; ctx.fillRect(0, 0, c.width, c.height);
    onChange("");
  };

  return (
    <div className="space-y-2">
      <canvas
        ref={canvasRef}
        width={600}
        height={200}
        className="w-full bg-white border border-[var(--border-line)] rounded-lg touch-none"
        onMouseDown={start} onMouseMove={move} onMouseUp={end} onMouseLeave={end}
        onTouchStart={start} onTouchMove={move} onTouchEnd={end}
        data-testid="signature-canvas"
      />
      <button type="button" onClick={clear} className="btn-ghost text-xs flex items-center gap-1">
        <RotateCcw size={12}/>Effacer
      </button>
    </div>
  );
}

// ---- Driver manager modal ----
function DriversModal({ onClose, isAdmin }) {
  const [drivers, setDrivers] = useState([]);
  const [form, setForm] = useState({ name: "", phone: "", vehicle: "" });

  const load = async () => {
    const { data } = await api.get("/drivers");
    setDrivers(data);
  };
  useEffect(() => { load(); }, []);

  const create = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) return toast.error("Nom requis");
    try {
      await api.post("/drivers", { ...form, active: true });
      setForm({ name: "", phone: "", vehicle: "" });
      load();
      toast.success("Chauffeur ajouté");
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
  };

  const remove = async (id) => {
    if (!window.confirm("Supprimer ce chauffeur ?")) return;
    await api.delete(`/drivers/${id}`);
    load();
    toast.success("Supprimé");
  };

  const shareLink = async (driver) => {
    try {
      const { data } = await api.post(`/drivers/${driver.id}/magic-link`);
      // Try to share via Web Share API first (mobile), fallback to opening wa.me
      const shareText = data.message;
      if (navigator.share && /Mobi|Android|iPhone|iPad/.test(navigator.userAgent)) {
        try {
          await navigator.share({ title: "Lien tournée chauffeur", text: shareText });
        } catch { /* user cancelled */ }
      } else {
        // Desktop / non-share-capable: open WhatsApp web with prefilled message
        window.open(data.whatsapp_url, "_blank", "noopener");
      }
      // Also copy raw link to clipboard for backup
      try { await navigator.clipboard.writeText(data.url); } catch {}
      toast.success("Lien généré — partagez-le via WhatsApp");
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
  };

  const copyLink = async (driver) => {
    try {
      const { data } = await api.post(`/drivers/${driver.id}/magic-link`);
      await navigator.clipboard.writeText(data.url);
      toast.success("Lien copié dans le presse-papier");
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
  };

  return (
    <div className="fixed inset-0 bg-black/50 z-50 overflow-y-auto" onClick={onClose}>
      <div className="min-h-full flex items-start sm:items-center justify-center p-3 sm:p-6">
        <div className="bg-white rounded-2xl max-w-lg w-full shadow-2xl" onClick={(e) => e.stopPropagation()} data-testid="drivers-modal">
          <div className="sticky top-0 z-10 bg-white rounded-t-2xl p-5 border-b border-[var(--border-line)] flex items-center justify-between">
            <div className="font-heading font-extrabold text-xl">Chauffeurs / Livreurs</div>
            <button onClick={onClose} className="w-9 h-9 rounded-full hover:bg-[var(--bg-soft,#f7f7f7)] flex items-center justify-center text-[var(--text-muted)]" aria-label="Fermer"><X size={22}/></button>
          </div>
          <div className="p-5 space-y-4">
          {isAdmin && (
            <form onSubmit={create} className="grid grid-cols-1 sm:grid-cols-3 gap-2 p-3 border border-dashed border-[var(--border-line)] rounded-lg" data-testid="new-driver-form">
              <input className="input-base sm:col-span-1" placeholder="Nom" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="driver-name"/>
              <input className="input-base" placeholder="Téléphone" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })}/>
              <div className="flex gap-2">
                <input className="input-base flex-1" placeholder="Véhicule" value={form.vehicle} onChange={(e) => setForm({ ...form, vehicle: e.target.value })}/>
                <button type="submit" className="btn-primary px-3" data-testid="add-driver-btn"><Plus size={14}/></button>
              </div>
            </form>
          )}
          {drivers.length === 0 ? <div className="text-[var(--text-muted)] text-sm text-center py-6">Aucun chauffeur</div> :
            <div className="space-y-2">
              {drivers.map((d) => (
                <div key={d.id} className="flex items-center justify-between gap-2 p-3 border border-[var(--border-line)] rounded-lg" data-testid={`driver-${d.id}`}>
                  <div className="min-w-0 flex-1">
                    <div className="font-semibold text-sm truncate">{d.name}</div>
                    <div className="font-data text-xs text-[var(--text-muted)] truncate">{d.phone || "—"} · {d.vehicle || "—"}</div>
                  </div>
                  {isAdmin && (
                    <div className="flex items-center gap-1 shrink-0">
                      <button onClick={() => shareLink(d)} title="Envoyer le lien tournée via WhatsApp" className="px-2 py-1.5 rounded-lg bg-[#25D366] hover:bg-[#1ebe5d] text-white flex items-center gap-1 text-xs font-semibold" data-testid={`share-driver-${d.id}`}>
                        <MessageCircle size={13}/>WhatsApp
                      </button>
                      <button onClick={() => copyLink(d)} title="Copier le lien" className="p-1.5 text-[var(--text-muted)] hover:text-[var(--primary)]" data-testid={`copy-driver-${d.id}`}><Copy size={14}/></button>
                      <button onClick={() => remove(d.id)} title="Supprimer" className="p-1.5 text-[var(--alert,#c32a2a)]"><Trash2 size={14}/></button>
                    </div>
                  )}
                </div>
              ))}
            </div>}
        </div>
        </div>
      </div>
    </div>
  );
}

// ---- Assign driver modal ----
function AssignModal({ order, drivers, onClose, onAssigned }) {
  const [driverId, setDriverId] = useState(order.assigned_driver_id || "");
  const [saving, setSaving] = useState(false);
  const submit = async () => {
    if (!driverId) return toast.error("Sélectionnez un chauffeur");
    setSaving(true);
    try {
      await api.post(`/customer-orders/${order.id}/assign-driver`, { driver_id: driverId });
      toast.success("Chauffeur assigné");
      onAssigned();
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
    finally { setSaving(false); }
  };
  return (
    <div className="fixed inset-0 bg-black/50 z-50 overflow-y-auto" onClick={onClose}>
      <div className="min-h-full flex items-start sm:items-center justify-center p-3 sm:p-6">
        <div className="bg-white rounded-2xl max-w-md w-full shadow-2xl" onClick={(e) => e.stopPropagation()} data-testid="assign-modal">
          <div className="sticky top-0 z-10 bg-white rounded-t-2xl p-5 border-b border-[var(--border-line)] flex items-center justify-between">
            <div className="min-w-0 flex-1 pr-2">
              <div className="font-heading font-extrabold text-xl truncate">Assigner un chauffeur</div>
              <div className="label-mono mt-1 truncate">{order.order_number} · {order.customer_name}</div>
            </div>
            <button onClick={onClose} className="w-9 h-9 rounded-full hover:bg-[var(--bg-soft,#f7f7f7)] flex items-center justify-center text-[var(--text-muted)] shrink-0" aria-label="Fermer"><X size={22}/></button>
          </div>
          <div className="p-5 space-y-3">
          {drivers.length === 0 ? (
            <div className="text-[var(--text-muted)] text-sm">Aucun chauffeur. Ajoutez-en un d'abord.</div>
          ) : drivers.filter((d) => d.active).map((d) => (
            <label key={d.id} className={`flex items-center gap-3 p-3 border rounded-lg cursor-pointer transition-colors ${driverId === d.id ? "border-[var(--primary,#C2569B)] bg-[var(--primary-soft,#F8E8F1)]" : "border-[var(--border-line)]"}`}>
              <input type="radio" name="drv" checked={driverId === d.id} onChange={() => setDriverId(d.id)} data-testid={`pick-driver-${d.id}`}/>
              <div className="flex-1">
                <div className="font-semibold text-sm">{d.name}</div>
                <div className="font-data text-xs text-[var(--text-muted)]">{d.phone || "—"} · {d.vehicle || "—"}</div>
              </div>
            </label>
          ))}
        </div>
        <div className="p-5 border-t border-[var(--border-line)] bg-white rounded-b-2xl">
          <button data-testid="assign-confirm" onClick={submit} disabled={saving} className="btn-primary w-full">{saving ? "..." : "Assigner"}</button>
        </div>
        </div>
      </div>
    </div>
  );
}

// ---- Complete delivery modal (signature + photo + cash collection) ----
function CompleteModal({ order, onClose, onDone }) {
  const [signature, setSignature] = useState("");
  const [photo, setPhoto] = useState("");
  const [notes, setNotes] = useState("");
  const [saving, setSaving] = useState(false);

  // Cash collection on delivery (if payment due)
  const balance = (order.total || 0) - (order.amount_paid || 0);
  const isCashOnDelivery = order.payment_method === "livraison" || order.payment_method === "comptant";
  const [collectPayment, setCollectPayment] = useState(isCashOnDelivery && balance > 0);
  const [paymentAmount, setPaymentAmount] = useState(balance > 0 ? balance : 0);
  const [paymentMethod, setPaymentMethod] = useState(isCashOnDelivery ? "especes" : "especes");
  const [paymentRef, setPaymentRef] = useState("");

  const onPhoto = (e) => {
    const f = e.target.files?.[0]; if (!f) return;
    const r = new FileReader();
    r.onload = () => setPhoto(r.result);
    r.readAsDataURL(f);
  };

  const submit = async () => {
    if (collectPayment) {
      const amt = Number(paymentAmount);
      if (!amt || amt <= 0) return toast.error("Montant invalide");
      if (amt > balance + 0.01) return toast.error("Montant supérieur au reste dû");
    }
    setSaving(true);
    try {
      // 1. Complete the delivery (generates BL + decrements stock)
      await api.post(`/customer-orders/${order.id}/complete-delivery`, {
        signature: signature || null, proof_photo: photo || null, notes,
      });
      // 2. Auto-invoice so we have an invoice_number ready for payment
      let needsInvoice = !order.invoice_number;
      if (needsInvoice) {
        try {
          await api.post(`/customer-orders/${order.id}/transition?action=facturer`);
        } catch (e) { /* may fail if already invoiced */ }
      }
      // 3. Record the payment if collected
      if (collectPayment) {
        await api.post(`/customer-orders/${order.id}/payment`, {
          amount: Number(paymentAmount),
          method: paymentMethod,
          note: paymentRef || `Encaissement à la livraison${order.assigned_driver_name ? ` par ${order.assigned_driver_name}` : ""}`,
        });
        toast.success(`Livré + ${Number(paymentAmount).toLocaleString("fr-FR")} F encaissés. Facture envoyée au client.`);
      } else {
        toast.success("Livraison confirmée. BL généré.");
      }
      onDone();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur");
    } finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 bg-black/50 z-50 overflow-y-auto" onClick={onClose}>
      <div className="min-h-full flex items-start sm:items-center justify-center p-3 sm:p-6">
        <div className="bg-white rounded-2xl max-w-lg w-full shadow-2xl" onClick={(e) => e.stopPropagation()} data-testid="complete-modal">
          <div className="sticky top-0 z-10 bg-white rounded-t-2xl p-4 sm:p-5 border-b border-[var(--border-line)] flex items-center justify-between">
            <div className="min-w-0 flex-1 pr-2">
              <div className="font-heading font-extrabold text-lg sm:text-xl truncate">Confirmer la livraison</div>
              <div className="label-mono mt-1 truncate">{order.order_number} · {order.customer_name}</div>
            </div>
            <button onClick={onClose} className="w-9 h-9 rounded-full hover:bg-[var(--bg-soft,#f7f7f7)] flex items-center justify-center text-[var(--text-muted)] shrink-0" aria-label="Fermer"><X size={22}/></button>
          </div>
          <div className="p-4 sm:p-5 space-y-4">
          <div>
            <label className="label-mono block mb-1.5">Signature client</label>
            <SignaturePad onChange={setSignature}/>
          </div>
          <div>
            <label className="label-mono block mb-1.5 flex items-center gap-1"><Camera size={12}/>Photo de preuve (optionnel)</label>
            <input type="file" accept="image/*" capture="environment" onChange={onPhoto} className="input-base text-xs" data-testid="proof-photo"/>
            {photo && <img src={photo} alt="preuve" className="mt-2 max-h-40 rounded-lg border border-[var(--border-line)]"/>}
          </div>

          {/* Cash collection block */}
          {balance > 0 && (
            <div className={`rounded-lg border-2 ${collectPayment ? "border-[var(--primary,#C2569B)] bg-[var(--primary-soft,#F8E8F1)]/40" : "border-dashed border-[var(--border-line)]"} p-3`} data-testid="cash-collection-block">
              <label className="flex items-start gap-2 cursor-pointer">
                <input type="checkbox" checked={collectPayment} onChange={(e) => setCollectPayment(e.target.checked)} className="mt-1" data-testid="collect-payment-toggle"/>
                <div className="flex-1">
                  <div className="font-semibold text-sm">Encaisser le règlement maintenant</div>
                  <div className="text-xs text-[var(--text-muted)] mt-0.5">
                    Reste dû : <span className="font-data font-bold">{balance.toLocaleString("fr-FR")} F CFA</span>
                    {isCashOnDelivery && <span className="ml-1 text-[var(--primary-dark,#8E3A6D)]">· Paiement à la livraison choisi</span>}
                  </div>
                </div>
              </label>
              {collectPayment && (
                <div className="mt-3 grid grid-cols-1 sm:grid-cols-2 gap-2">
                  <div>
                    <label className="label-mono block mb-1">Montant reçu (F CFA)</label>
                    <input type="number" data-testid="delivery-payment-amount" className="input-base font-data" value={paymentAmount} onChange={(e) => setPaymentAmount(e.target.value)} max={balance} min="0"/>
                  </div>
                  <div>
                    <label className="label-mono block mb-1">Mode</label>
                    <select data-testid="delivery-payment-method" className="input-base" value={paymentMethod} onChange={(e) => setPaymentMethod(e.target.value)}>
                      <option value="especes">Espèces (à la livraison)</option>
                      <option value="virement">Virement bancaire</option>
                      <option value="cheque">Chèque</option>
                      <option value="autre">Autre</option>
                    </select>
                  </div>
                  <div className="sm:col-span-2">
                    <label className="label-mono block mb-1">Référence (optionnel)</label>
                    <input type="text" className="input-base" value={paymentRef} onChange={(e) => setPaymentRef(e.target.value)} placeholder="N° chèque, référence virement..."/>
                  </div>
                </div>
              )}
            </div>
          )}

          <div>
            <label className="label-mono block mb-1.5">Notes</label>
            <textarea rows={2} className="input-base" value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Remarques éventuelles..."/>
          </div>
        </div>
        <div className="p-4 sm:p-5 border-t border-[var(--border-line)] bg-white rounded-b-2xl">
          <button data-testid="confirm-delivery" onClick={submit} disabled={saving} className="btn-primary w-full flex items-center justify-center gap-2">
            <CheckCircle2 size={16}/>
            {saving ? "Enregistrement..." : (collectPayment ? `Livrer & encaisser ${Number(paymentAmount || 0).toLocaleString("fr-FR")} F` : "Confirmer la livraison")}
          </button>
        </div>
        </div>
      </div>
    </div>
  );
}

// ---- Main page ----
export default function Deliveries() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [orders, setOrders] = useState([]);
  const [drivers, setDrivers] = useState([]);
  const [filter, setFilter] = useState("all");
  const [showDrivers, setShowDrivers] = useState(false);
  const [assignTarget, setAssignTarget] = useState(null);
  const [completeTarget, setCompleteTarget] = useState(null);

  // Body scroll lock when any modal open
  useEffect(() => {
    const open = showDrivers || !!assignTarget || !!completeTarget;
    if (open) {
      const prev = document.body.style.overflow;
      document.body.style.overflow = "hidden";
      return () => { document.body.style.overflow = prev; };
    }
  }, [showDrivers, assignTarget, completeTarget]);

  const load = async () => {
    const [d, dr] = await Promise.all([api.get("/deliveries"), api.get("/drivers")]);
    setOrders(d.data); setDrivers(dr.data);
  };
  useEffect(() => { load(); }, []);

  const filtered = useMemo(() => {
    if (filter === "all") return orders;
    return orders.filter((o) => getDeliveryState(o) === filter);
  }, [orders, filter]);

  const stats = useMemo(() => {
    const c = { en_attente: 0, assigne: 0, en_route: 0, livree: 0, echec: 0 };
    orders.forEach((o) => { c[getDeliveryState(o)] = (c[getDeliveryState(o)] || 0) + 1; });
    return c;
  }, [orders]);

  const startDelivery = async (o) => {
    try {
      await api.post(`/customer-orders/${o.id}/start-delivery`);
      toast.success("Livraison démarrée");
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
  };

  const failDelivery = async (o) => {
    const reason = window.prompt("Motif de l'échec ?");
    if (!reason) return;
    try {
      await api.post(`/customer-orders/${o.id}/fail-delivery`, { reason });
      toast.success("Échec enregistré");
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
  };

  const resetDelivery = async (o) => {
    try {
      await api.post(`/customer-orders/${o.id}/reset-delivery`);
      toast.success("Réinitialisé");
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
  };

  const filters = [
    { k: "all", label: "Toutes", n: orders.length },
    { k: "en_attente", label: "À assigner", n: stats.en_attente },
    { k: "assigne", label: "Assignées", n: stats.assigne },
    { k: "en_route", label: "En route", n: stats.en_route },
    { k: "livree", label: "Livrées", n: stats.livree },
    { k: "echec", label: "Échecs", n: stats.echec },
  ];

  return (
    <div className="space-y-5">
      <PageHeader
        title="Livraisons & Tournées"
        subtitle="Suivi temps réel · Chauffeurs"
        testId="deliveries-header"
        actions={
          <button onClick={() => setShowDrivers(true)} className="btn-ghost flex items-center gap-2" data-testid="manage-drivers">
            <UserPlus size={14}/>Chauffeurs ({drivers.length})
          </button>
        }
      />

      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3" data-testid="delivery-stats">
        <StatCard label="À assigner" value={stats.en_attente} accent="warn" icon={Truck}/>
        <StatCard label="Assignées" value={stats.assigne} icon={UserPlus}/>
        <StatCard label="En route" value={stats.en_route} accent="warn" icon={Truck}/>
        <StatCard label="Livrées (jour)" value={stats.livree} accent="green" icon={CheckCircle2}/>
        <StatCard label="Échecs" value={stats.echec} accent="pink" icon={XCircle}/>
      </div>

      <div className="flex flex-wrap items-center gap-2" data-testid="delivery-filters">
        {filters.map((f) => (
          <button key={f.k} onClick={() => setFilter(f.k)} className={`px-3 py-1.5 rounded-full text-xs font-semibold border transition-colors ${filter === f.k ? "bg-[var(--primary,#C2569B)] text-white border-[var(--primary,#C2569B)]" : "bg-white border-[var(--border-line)] text-[var(--text-muted)] hover:border-[var(--primary,#C2569B)]/50"}`} data-testid={`filter-${f.k}`}>
            {f.label} <span className="ml-1 opacity-80">{f.n}</span>
          </button>
        ))}
      </div>

      {filtered.length === 0 ? (
        <Surface><Empty message="Aucune commande en livraison" /></Surface>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-3" data-testid="delivery-cards">
          {filtered.map((o) => {
            const state = getDeliveryState(o);
            const meta = STATUS_LABELS[state];
            return (
              <div key={o.id} className="surface p-4" data-testid={`delivery-${o.order_number}`}>
                <div className="flex items-start justify-between gap-3 mb-3">
                  <div className="min-w-0 flex-1">
                    <div className="font-heading font-bold text-base">{o.order_number}</div>
                    <div className="font-medium truncate">{o.customer_name}</div>
                    <div className="flex items-center gap-3 text-xs text-[var(--text-muted)] mt-1 flex-wrap">
                      {o.customer_phone && <span className="flex items-center gap-1"><Phone size={11}/>{o.customer_phone}</span>}
                      {o.customer_address && <span className="flex items-center gap-1"><MapPin size={11}/>{o.customer_address}</span>}
                    </div>
                  </div>
                  <span className={`shrink-0 text-xs font-semibold px-2 py-1 rounded-full ${meta.cls}`}>{meta.label}</span>
                </div>

                <div className="flex items-center justify-between text-sm py-2 border-y border-[var(--border-line)] mb-3">
                  <div>
                    <span className="label-mono">Total · </span>
                    <span className="font-data font-bold">{fmt(o.total)}</span>
                  </div>
                  <div className="text-xs text-[var(--text-muted)]">
                    {o.assigned_driver_name ? <>Chauffeur : <b className="text-[var(--text-base)]">{o.assigned_driver_name}</b></> : <span className="italic">Aucun chauffeur</span>}
                  </div>
                </div>

                {o.delivery_failure_reason && (
                  <div className="bg-red-50 border border-red-200 text-red-800 text-xs p-2 rounded-lg mb-3">
                    Échec : {o.delivery_failure_reason}
                  </div>
                )}

                <div className="flex flex-wrap gap-2">
                  {state !== "livree" && (
                    <button onClick={() => setAssignTarget(o)} className="btn-ghost text-xs flex items-center gap-1" data-testid={`assign-${o.order_number}`}>
                      <UserPlus size={12}/>{o.assigned_driver_id ? "Changer" : "Assigner"}
                    </button>
                  )}
                  {state === "assigne" && (
                    <button onClick={() => startDelivery(o)} className="btn-primary text-xs flex items-center gap-1" data-testid={`start-${o.order_number}`}>
                      <PlayCircle size={12}/>Démarrer
                    </button>
                  )}
                  {(state === "assigne" || state === "en_route") && (
                    <button onClick={() => setCompleteTarget(o)} className="btn-primary text-xs flex items-center gap-1" data-testid={`complete-${o.order_number}`}>
                      <CheckCircle2 size={12}/>Confirmer livraison
                    </button>
                  )}
                  {(state === "assigne" || state === "en_route") && (
                    <button onClick={() => failDelivery(o)} className="btn-ghost text-xs flex items-center gap-1 text-[var(--alert,#c32a2a)]" data-testid={`fail-${o.order_number}`}>
                      <XCircle size={12}/>Échec
                    </button>
                  )}
                  {state === "echec" && (
                    <button onClick={() => resetDelivery(o)} className="btn-ghost text-xs flex items-center gap-1" data-testid={`reset-${o.order_number}`}>
                      <RotateCcw size={12}/>Réessayer
                    </button>
                  )}
                  {state === "livree" && o.delivery_number && (
                    <a href={`${process.env.REACT_APP_BACKEND_URL}/api/customer-orders/${o.id}/pdf/bl`} target="_blank" rel="noopener noreferrer" className="btn-ghost text-xs">BL PDF</a>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {showDrivers && <DriversModal isAdmin={isAdmin} onClose={() => { setShowDrivers(false); load(); }}/>}
      {assignTarget && <AssignModal order={assignTarget} drivers={drivers} onClose={() => setAssignTarget(null)} onAssigned={() => { setAssignTarget(null); load(); }}/>}
      {completeTarget && <CompleteModal order={completeTarget} onClose={() => setCompleteTarget(null)} onDone={() => { setCompleteTarget(null); load(); }}/>}
    </div>
  );
}
